import { INITIAL_RESUME } from './constants.js';

export const parseResumeText = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    const result = { ...INITIAL_RESUME, personal: { ...INITIAL_RESUME.personal }, experience: [], education: [], skills: [] };

    if (lines.length === 0) return result;

    // --- 1. Personal Details (Top of Document) ---

    // Name: First non-empty line usually
    const nameLine = lines[0];
    if (nameLine && !nameLine.toLowerCase().includes('resume')) {
        result.personal.fullName = nameLine;
    }

    // Extract from first 10 lines for Contact Info
    const headerText = lines.slice(0, 10).join(' ');

    // Email
    const emailMatch = headerText.match(/\b[\w.-]+@[\w.-]+\.\w{2,4}\b/);
    if (emailMatch) result.personal.email = emailMatch[0];

    // Phone: Improved Regex for Int'l formats like +971 ...
    const phoneMatch = headerText.match(/(?:\+?\d{1,3}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{3,4}[-.\s]?\d{3,9}/);
    if (phoneMatch) result.personal.phone = phoneMatch[0];

    // Location: Heuristic - look for "|" separators often used in headers
    // e.g. "Dubai, UAE | +971..."
    const locationLine = lines.find(l => l.includes('|') || l.includes('•'));
    if (locationLine) {
        const parts = locationLine.split(/[|•]/).map(p => p.trim());
        // Assume location is the part that isn't email or phone
        const locPart = parts.find(p =>
            !p.includes('@') &&
            !/\d{5,}/.test(p) &&
            !p.includes('linkedin') &&
            p.length > 2
        );
        if (locPart) result.personal.location = locPart;
    }

    // --- 2. Section Detection ---
    // We identify indices where sections start
    const sections = {
        summary: -1,
        experience: -1,
        education: -1,
        skills: -1
    };

    lines.forEach((line, index) => {
        const lower = line.toLowerCase();
        // strict matching to avoid false positives in descriptions
        if (lower === 'summary' || lower.startsWith('professional summary')) sections.summary = index;
        else if (lower === 'experience' || lower === 'work experience' || lower === 'employment history' || lower === 'professional experience') sections.experience = index;
        else if (lower === 'education' || lower === 'academic background') sections.education = index;
        else if (lower === 'skills' || lower === 'technical skills' || lower === 'technologies') sections.skills = index;
    });

    // Helper to get lines between two sections
    const getSectionLines = (startIdx, sectionKeys) => {
        if (startIdx === -1) return [];
        // Find the next section that appears after this one
        const nextIndices = Object.values(sectionKeys)
            .filter(idx => idx > startIdx)
            .sort((a, b) => a - b);

        const endIdx = nextIndices.length > 0 ? nextIndices[0] : lines.length;
        return lines.slice(startIdx + 1, endIdx);
    };

    // --- 3. Parse Summary ---
    const summaryLines = getSectionLines(sections.summary, sections);
    if (summaryLines.length > 0) {
        result.personal.summary = summaryLines.join(' ');
    }

    // --- 4. Parse Experience (Complex) ---
    const expLines = getSectionLines(sections.experience, sections);
    let currentJob = null;

    // Date Regex: Matches "May 2023 – Present", "2018 - 2019", "Jan 2020", "Mar 2025", etc.
    // Handles various dashes: -, –, — and case variations
    const dateRegex = /((?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{4}|\d{4})\s*[-–—]\s*((?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{4}|present|current|now|\d{4})/i;

    for (let i = 0; i < expLines.length; i++) {
        const line = expLines[i];
        // Increased length check slightly, some date lines might have other text
        const isDateLine = dateRegex.test(line);

        // If we find a date line, the line BEFORE it is likely the Company/Role
        if (isDateLine) {
            // Save previous job if exists
            if (currentJob) {
                result.experience.push(currentJob);
            }

            // Initialize new job
            currentJob = {
                id: Date.now() + i,
                role: "",
                company: "",
                startDate: "",
                endDate: "",
                description: "",
                location: ""
            };

            // Parse Date Line - Extract dates
            const match = line.match(dateRegex);
            if (match && match[1] && match[2]) {
                currentJob.startDate = match[1];
                currentJob.endDate = match[2];
            }

            // Check if company/role are on the SAME line (inline format)
            // Format: "Company – Role | Date – Date" or "Company – Role | Date"
            if (line.includes('|')) {
                // Split by pipe to separate header from dates
                const parts = line.split('|').map(s => s.trim());
                const headerPart = parts[0]; // "Company – Role"

                // Now split header by em-dash or hyphen
                const headerSeparators = /\s*[—–]\s*/;
                if (headerSeparators.test(headerPart)) {
                    const headerParts = headerPart.split(headerSeparators).map(s => s.trim());
                    currentJob.company = headerParts[0] || "";
                    currentJob.role = headerParts[1] || "";
                } else {
                    currentJob.company = headerPart;
                }
            } else {
                // Multi-line format: Look at previous line for Company/Role
                if (i > 0) {
                    const headerLine = expLines[i - 1];
                    const separators = /\s*[—–-]\s*/;
                    if (separators.test(headerLine)) {
                        const parts = headerLine.split(separators).map(s => s.trim());
                        currentJob.company = parts[0];
                        currentJob.role = parts[1] || parts[0];
                    } else {
                        currentJob.company = headerLine;
                    }
                }
            }
        } else if (currentJob) {
            // It's part of the description
            // Handle "Technologies:" line specifically
            if (line.toLowerCase().startsWith('technologies:')) {
                currentJob.description += `\n\n**${line}**`;
            } else {
                currentJob.description += (currentJob.description ? "\n" : "") + line;
            }
        }
    }
    // Push the last job found
    if (currentJob) result.experience.push(currentJob);


    // --- 5. Parse Education ---
    const eduLines = getSectionLines(sections.education, sections);
    // Heuristic: chunks of 2-3 lines usually. Look for School names or Degrees.
    // We'll simplify: Every time we see a year, we assume it's a new entry
    let currentEdu = null;

    for (let i = 0; i < eduLines.length; i++) {
        const line = eduLines[i];
        // Check for year pattern like "2013 – 2015" or "2015"
        const yearMatch = line.match(/\d{4}/);

        if (yearMatch) {
            if (currentEdu) result.education.push(currentEdu);

            currentEdu = {
                id: Date.now() + i,
                school: "",
                degree: "",
                year: yearMatch[0],
                location: ""
            };

            // Previous line is likely School/Degree info
            if (i > 0) {
                currentEdu.school = eduLines[i - 1];
                // Try to find degree in the current line or previous
                if (line.toLowerCase().includes('degree') || line.toLowerCase().includes('bachelor') || line.toLowerCase().includes('master')) {
                    currentEdu.degree = line.split(/[•|]/)[0].trim(); // Remove year if attached
                }
            } else {
                currentEdu.school = line;
            }
        } else if (currentEdu) {
            // Append extra info to degree if it looks like degree info
            if (line.toLowerCase().includes('degree') || line.toLowerCase().includes('computer science')) {
                currentEdu.degree += (currentEdu.degree ? ", " : "") + line;
            }
        }
    }
    if (currentEdu) result.education.push(currentEdu);


    // --- 6. Parse Skills ---
    const skillLines = getSectionLines(sections.skills, sections);
    const rawSkills = [];

    skillLines.forEach(line => {
        // Remove "Programming:", "Technologies:" prefixes
        const cleanLine = line.replace(/^[•\-*]\s*/, '').replace(/.*:\s*/, '');
        // Split by commas
        const items = cleanLine.split(',').map(s => s.trim()).filter(s => s.length > 1);
        rawSkills.push(...items);
    });

    // Dedupe
    result.skills = [...new Set(rawSkills)];

    return result;
};
